import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Play, ChevronDown, ChevronUp } from "lucide-react";
import { useSetupProgress } from "../../Moduels/Context/SetupProgressContext";
import "../../styles/setupProgress.css";

const DETAILS_OPEN_KEY = "brixigo.setupProgress.detailsOpen";

function readDetailsPreference() {
  try {
    const v = sessionStorage.getItem(DETAILS_OPEN_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    /* ignore */
  }
  return null;
}

function writeDetailsPreference(open) {
  try {
    sessionStorage.setItem(DETAILS_OPEN_KEY, open ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export default function SetupProgressCard({ showRunPayroll = true }) {
  const { progress, refetch } = useSetupProgress();
  const navigate = useNavigate();
  const [detailsOpen, setDetailsOpen] = useState(true);
  const prevPercentRef = useRef(null);

  useEffect(() => {
    refetch?.();
  }, [refetch]);

  const percent = progress?.percent ?? 0;
  const isComplete = percent >= 100;

  useEffect(() => {
    if (!progress || progress.loading) return;

    if (!isComplete) {
      setDetailsOpen(true);
      prevPercentRef.current = percent;
      return;
    }

    const prev = prevPercentRef.current;
    const justCompleted = prev != null && prev < 100;

    if (justCompleted) {
      setDetailsOpen(false);
      writeDetailsPreference(false);
    } else {
      const pref = readDetailsPreference();
      setDetailsOpen(pref === null ? false : pref);
    }

    prevPercentRef.current = percent;
  }, [progress, progress?.loading, isComplete, percent]);

  const toggleDetails = () => {
    setDetailsOpen((open) => {
      const next = !open;
      writeDetailsPreference(next);
      return next;
    });
  };

  if (!progress || progress.loading) {
    return (
      <section className="sp-card sp-card-loading">
        <div className="sp-shimmer" />
      </section>
    );
  }

  const seenIds = new Set();
  const items = (progress.items || []).filter((item) => {
    if (!item?.id || seenIds.has(item.id)) return false;
    seenIds.add(item.id);
    return true;
  });
  const currentStepId = progress.currentStepId;
  const updatedAt = progress.updated_at
    ? new Date(progress.updated_at).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Just now";

  const nextItem = items.find((i) => i.id === currentStepId) || items.find((i) => !i.complete);
  const payrollSub =
    !isComplete && nextItem
      ? `Next: Step ${nextItem.step || ""} — ${nextItem.label}`
      : !isComplete && progress.payrollReadiness && !progress.payrollReadiness.complete
        ? `Payroll pipeline: ${progress.payrollReadiness.percent}% — ${progress.payrollReadiness.current_step_label || "setup in progress"}.`
        : isComplete && !detailsOpen
          ? "All setup steps are complete — expand to review steps."
          : null;

  const showChecklist = !isComplete || detailsOpen;

  return (
    <section
      className={`sp-card sp-card-ref${isComplete ? " sp-complete" : ""}${isComplete && !detailsOpen ? " sp-card-collapsed" : ""}`}
    >
      <div className={`sp-ref-top${!showChecklist ? " sp-ref-top--compact" : ""}`}>
        <div className="sp-ref-left">
          <div className="sp-ref-label">
            {isComplete ? (
              <>
                <CheckCircle2 size={15} className="sp-icon-done" />
                Setup Complete
              </>
            ) : (
              "Setup Progress"
            )}
          </div>
          {payrollSub ? <p className="sp-sub sp-ref-sub">{payrollSub}</p> : null}
          <div className="sp-ref-bar-row">
            <div className="sp-progress-bar sp-ref-bar">
              <div
                className={`sp-progress-fill${isComplete ? " sp-fill-done" : ""}`}
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className={`sp-ref-pct${isComplete ? " done" : ""}`}>{percent}%</span>
          </div>
        </div>

        <div className="sp-ref-right">
          <span className="sp-ref-updated">Last updated · {updatedAt}</span>
          {showRunPayroll ? (
            <button
              type="button"
              className="sp-ref-run"
              onClick={() => navigate("/process-payroll")}
            >
              <Play size={14} /> Run Payroll
            </button>
          ) : null}
        </div>
      </div>

      {showChecklist ? (
        <ol className="sp-ref-checklist sp-ref-steps">
          {items.map((item, idx) => {
            const isCurrent = !item.complete && item.id === currentStepId;
            const locked =
              !item.complete &&
              currentStepId &&
              item.id !== currentStepId &&
              items.findIndex((i) => i.id === currentStepId) < idx;
            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={`sp-ref-check${item.complete ? " done" : ""}${isCurrent ? " current" : ""}${locked ? " upcoming" : ""}`}
                  title={item.description || item.label}
                >
                  <span className="sp-step-num">{item.step || idx + 1}</span>
                  {item.complete ? (
                    <CheckCircle2 size={15} className="sp-icon-done" />
                  ) : (
                    <Circle size={15} className="sp-icon-pending" />
                  )}
                  <span className="sp-step-label">{item.label}</span>
                  {!item.complete ? <ArrowRight size={12} className="sp-arrow" /> : null}
                </Link>
              </li>
            );
          })}
        </ol>
      ) : null}

      {isComplete ? (
        <button type="button" className="sp-collapse-link" onClick={toggleDetails}>
          {detailsOpen ? (
            <>
              Collapse
              <ChevronUp size={13} />
            </>
          ) : (
            <>
              View setup steps
              <ChevronDown size={13} />
            </>
          )}
        </button>
      ) : null}
    </section>
  );
}
